import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { cn } from 'utils/cn';

export type FigureColumn<Row> = {
    id: string;
    header: string;
    /** A figure is right-aligned in tabular digits. */
    numeric?: boolean;
    cell: (row: Row) => React.ReactNode;
};

type Props<Row> = {
    rows: Row[];
    columns: FigureColumn<Row>[];
    rowKey: (row: Row) => string;
    label: string;
};

/** A report's rows as a plain table in the order the report returns them, its first column the row's name. */
export function FigureTable<Row>({ rows, columns, rowKey, label }: Props<Row>) {
    return (
        <Table aria-label={label} className="text-meta tabular-nums">
            <TableHeader>
                <TableRow className="bg-muted hover:bg-muted">
                    {columns.map((column, index) => (
                        <TableHead
                            key={column.id}
                            scope="col"
                            className={cn(
                                'text-muted-foreground px-3 font-medium',
                                column.numeric && 'text-right',
                                index === 0 && 'bg-muted sticky left-0'
                            )}
                        >
                            {column.header}
                        </TableHead>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={rowKey(row)}>
                        {columns.map((column, index) =>
                            index === 0 ? (
                                <TableHead
                                    key={column.id}
                                    scope="row"
                                    className="bg-card sticky left-0 h-auto px-3 py-1.5 font-medium"
                                >
                                    {column.cell(row)}
                                </TableHead>
                            ) : (
                                <TableCell
                                    key={column.id}
                                    className={cn('px-3 py-1.5', column.numeric && 'text-right')}
                                >
                                    {column.cell(row)}
                                </TableCell>
                            )
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
